import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  QueryList,
  viewChild
} from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, animationFrameScheduler, fromEvent } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  tap,
  throttleTime,
  withLatestFrom
} from 'rxjs/operators';
import { MatSidenav } from '@angular/material/sidenav';
import { MatListItem } from '@angular/material/list';
import { NavigationStart, QueryParamsHandling, Router } from '@angular/router';
import { EventService } from '../../services/event.service';
import { SIDEBAR_LINKS } from '../../constants/layout.constants';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContentWrapperModule } from '../../modules/content-wrapper.module';
import { BaseComponent } from '../../abstract-components/base.component';

@Component({
  selector: 'content-wrapper',
  standalone: true,
  imports: [ContentWrapperModule],
  templateUrl: './content-wrapper.component.html',
  styleUrls: ['./content-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContentWrapperComponent extends BaseComponent implements OnInit {
  private _breakpointObserver = inject(BreakpointObserver);
  private _eventService = inject(EventService);
  private _router = inject(Router);
  private _sanitizer = inject(DomSanitizer);
  private _appRef = inject(ApplicationRef);
  protected _destroyRef = inject(DestroyRef);

  drawer = viewChild<MatSidenav>(MatSidenav);
  toolbar = viewChild<ElementRef<HTMLElement>>('toolbar');
  listItems = viewChild<QueryList<MatListItem>>('listItems');

  isHandset$: Observable<boolean> = this._breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(
      map(result => result.matches),
      shareReplay()
    );

  isWindowAtTop$ = fromEvent(window, 'scroll').pipe(
    takeUntilDestroyed(this._destroyRef),
    startWith(false),
    throttleTime(0, animationFrameScheduler),
    map(() => window.scrollY === 0),
    distinctUntilChanged()
  );

  isLayoutHidden$ = this._globalFacade.routeData$.pipe(
    map(routeData =>
      routeData ? routeData.data?.layoutHidden === true : false
    ),
    startWith(true)
  );

  closeHandsetSidebarOnRouteChange$ = this._router.events.pipe(
    withLatestFrom(this.isHandset$),
    filter(([a, b]) => b && a instanceof NavigationStart),
    tap(() => this.drawer()?.close())
  );

  onToggleMenu$ = this._eventService.events$.pipe(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    filter(ev => ev.type === 'TOGGLE_MENU'),
    withLatestFrom(this.isHandset$),
    tap(() => this.drawer()?.toggle())
  );

  sidebarLinks = SIDEBAR_LINKS;
  isSidenavCollapsed = false;

  ngOnInit(): void {
    this._authFacade.resetLoginState();
    this._authFacade.resetOnboardingForms();
    this._authFacade.hydrateLoggedUserProfile();
  }

  toggleSidenav(): void {
    this.isSidenavCollapsed = !this.isSidenavCollapsed;
    setTimeout(() => {
      this._appRef.tick();
    }, 100);
  }

  sanitizeHtml(html: string): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(html);
  }

  getQueryParamsHandling(
    link: (typeof SIDEBAR_LINKS)[number]
  ): QueryParamsHandling | undefined {
    if (!link.preserveQueryParams) return;
    const currentPath = this._router.url.split('?')[0];
    return currentPath.startsWith(link.url) ? 'merge' : undefined;
  }

  get toolbarHeight(): number | undefined {
    if (!this.toolbar()?.nativeElement) return;
    return this.toolbar()?.nativeElement.clientHeight || 0;
  }
}
